import {
  AssignerPrograms,
  CaseDetails,
  CaseNote,
  EconsultSpecialist,
  NavigationAction,
  TimeOption
} from '@oh-vcp/components-common';
import React, {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react';
import { CaseFolderPage } from '@oh-vcp/components-common/src/models/case/CaseDetails';

interface EconsultCaseDetailsContextProps {
  caseDetails: CaseDetails | undefined;
  draftCaseSaveDisable: boolean | false;
  draftCaseSaved: boolean | false;
  returnNoteSaved: boolean | false;
  caseAssignChanged: boolean | false;
  draftCaseOrNoteChanged: boolean | false;
  draftNote: CaseNote | undefined;
  userRole: string | undefined;
  userRoleDual: string | undefined;
  timeOptions: TimeOption[] | undefined;
  caseFolderPages: CaseFolderPage[] | undefined;
  setCaseDetails: React.Dispatch<React.SetStateAction<CaseDetails | undefined>>;
  setDraftCaseSaveDisable: (draftCaseSaveDisable: boolean | false) => void;
  setDraftCaseSaved: (draftCaseSaved: boolean | false) => void;
  setReturnNoteSaved: (returnNoteSaved: boolean | false) => void;
  setCaseAssignChanged: (caseAssignChanged: boolean | false) => void;
  setDraftCaseOrNoteChanged: (draftCaseOrNoteChanged: boolean | false) => void;
  setDraftNote: (draftNote: CaseNote | undefined) => void;
  setTimeOptions: (options: TimeOption[] | undefined) => void;
  setCaseFolderPages: (caseFolderPages: CaseFolderPage[] | undefined) => void;
  setUserRole: (userRole: string | undefined) => void;
  setUserRoleDual: (userRoleDual: string | undefined) => void;
  assignerPrograms: AssignerPrograms | undefined;
  setAssignerPrograms: React.Dispatch<React.SetStateAction<AssignerPrograms | undefined>>;
  navigationAction: NavigationAction | undefined;
  setNavigationAction: (action: NavigationAction | undefined) => void;
  fileUploading: boolean;
  setFileUploading: React.Dispatch<React.SetStateAction<boolean>>;
  abortControllers: React.MutableRefObject<Map<string, AbortController>>;
  abortAllUploads: () => void;
  cancelUploads: React.MutableRefObject<boolean>;
  currentFolderUrl: string | undefined;
  setCurrentFolderUrl: React.Dispatch<React.SetStateAction<string | undefined>>;
  programSpecialists: Record<number, EconsultSpecialist[]>;
  setProgramSpecialists: (progSpecialists: Record<number, EconsultSpecialist[]>) => void;
}

export const EconsultCaseDetailsContext = createContext<
  EconsultCaseDetailsContextProps | undefined
>(undefined);

export const useEconsultCaseDetailsContext = () => {
  const value = useContext(EconsultCaseDetailsContext);
  if (!value) {
    throw new Error(
      'no value found for useEconsultCaseDetailsContext, did you forget to wrap it in a provider?'
    );
  }
  return value;
};

export const EconsultCaseDetailsProvider: FC<PropsWithChildren> = ({ children }) => {
  const [caseDetails, setCaseDetails] = useState<CaseDetails | undefined>(undefined);
  const [draftCaseSaveDisable, setDraftCaseSaveDisable] = useState<boolean | false>(false);
  const [draftCaseSaved, setDraftCaseSaved] = useState<boolean | false>(false);
  const [returnNoteSaved, setReturnNoteSaved] = useState<boolean | false>(false);
  const [caseAssignChanged, setCaseAssignChanged] = useState<boolean | false>(false);
  const [draftCaseOrNoteChanged, setDraftCaseOrNoteChanged] = useState<boolean | false>(false);
  const [draftNote, setDraftNote] = useState<CaseNote | undefined>(undefined);
  const [timeOptions, setTimeOptions] = useState<TimeOption[] | undefined>(undefined);
  const [caseFolderPages, setCaseFolderPages] = useState<CaseFolderPage[] | undefined>(undefined);
  const [userRole, setUserRole] = useState<string | undefined>(undefined);
  const [userRoleDual, setUserRoleDual] = useState<string | undefined>(undefined);
  const [assignerPrograms, setAssignerPrograms] = useState<AssignerPrograms | undefined>(undefined);
  const [navigationAction, setNavigationAction] = useState<NavigationAction | undefined>(undefined);
  const [fileUploading, setFileUploading] = useState<boolean>(false);
  const [currentFolderUrl, setCurrentFolderUrl] = useState<string | undefined>(undefined);
  const abortControllers = useRef<Map<string, AbortController>>(new Map());
  const cancelUploads = useRef<boolean>(false);
  const [programSpecialists, setProgramSpecialists] = useState<
    Record<number, EconsultSpecialist[]>
  >({});

  const abortAllUploads = useCallback(() => {
    abortControllers.current.forEach((controller) => {
      controller.abort();
    });
    abortControllers.current.clear();
    cancelUploads.current = true;
  }, []);

  const value = useMemo(() => {
    return {
      caseDetails,
      setCaseDetails,
      draftCaseSaveDisable,
      setDraftCaseSaveDisable,
      draftCaseSaved,
      setDraftCaseSaved,
      returnNoteSaved,
      setReturnNoteSaved,
      caseAssignChanged,
      setCaseAssignChanged,
      draftCaseOrNoteChanged,
      setDraftCaseOrNoteChanged,
      draftNote,
      setDraftNote,
      timeOptions,
      setTimeOptions,
      caseFolderPages,
      setCaseFolderPages,
      userRole,
      setUserRole,
      userRoleDual,
      setUserRoleDual,
      assignerPrograms,
      setAssignerPrograms,
      navigationAction,
      setNavigationAction,
      fileUploading,
      setFileUploading,
      abortControllers,
      abortAllUploads,
      cancelUploads,
      currentFolderUrl,
      setCurrentFolderUrl,
      programSpecialists,
      setProgramSpecialists
    };
  }, [
    caseDetails,
    draftCaseSaveDisable,
    draftCaseSaved,
    returnNoteSaved,
    caseAssignChanged,
    draftCaseOrNoteChanged,
    draftNote,
    timeOptions,
    caseFolderPages,
    userRole,
    userRoleDual,
    assignerPrograms,
    navigationAction,
    fileUploading,
    abortControllers,
    currentFolderUrl,
    programSpecialists
  ]);

  return (
    <EconsultCaseDetailsContext.Provider value={value}>
      {children}
    </EconsultCaseDetailsContext.Provider>
  );
};
